import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useActivityContext } from '../index';
import { API_CONFIG } from '../../../../../config/api';

const PartFPage1 = () => {
  const { activity } = useActivityContext();
  const [formData, setFormData] = useState({
    group_project_work: {
      research_prompt: 0,
      guiding_questions: 0,
      stage_1_brainstorming: 0,
      stage_2_drafting: 0,
      stage_3_final_submission: 0
    },
    problem_based_inquiry: {
      project_prompt: 0,
      hypothesis: 0,
      guiding_questions: 0,
      evidence_collection: 0,
      analysis_synthesis: 0,
      discussions: 0,
      conclusion: 0
    },
    classroom_interactions: {
      classroom_discussion: 0,
      organised_debate: 0,
      simulation_roleplay: 0,
      lab_experiment: 0,
      digital_learning: 0
    }
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (activity?.my_response_part_f?.page_1) {
      const savedData = activity.my_response_part_f.page_1;
      setFormData({
        group_project_work: savedData.group_project_work || formData.group_project_work,
        problem_based_inquiry: savedData.problem_based_inquiry || formData.problem_based_inquiry,
        classroom_interactions: savedData.classroom_interactions || formData.classroom_interactions
      });
    }
  }, [activity]);

  const saveChanges = async (updatedData) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');
      const apiUrl = API_CONFIG.BASE_URL;

      await axios.put(
        `${apiUrl}/secondary/user/domain-activities/${activity.activity_id}/part-f/page-1/student-fields`,
        updatedData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSaveMessage('Saved ✓');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error saving:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    const updatedData = {
      ...formData,
      [section]: {
        ...formData[section],
        [field]: parseInt(value) || 0
      }
    };
    setFormData(updatedData);

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveChanges(updatedData), 1000);
  };

  const calculateSectionTotal = (section) => {
    return Object.values(formData[section]).reduce((sum, val) => sum + (val || 0), 0);
  };

  const renderInput = (section, field, value) => (
    <input
      type="number"
      min="0"
      value={value || ''}
      onChange={(e) => handleInputChange(section, field, e.target.value)}
      placeholder="0"
      style={{
        width: '100%',
        padding: '8px',
        border: 'none',
        backgroundColor: 'transparent',
        fontSize: '14px',
        color: '#333',
        textAlign: 'center',
        outline: 'none'
      }}
    />
  );

  return (
    <div style={{
      maxWidth: '1400px',       // Increased from 1200px for wider layout  
      margin: '0 auto',
      padding: '40px',
      backgroundColor: 'white',
      fontFamily: "'Poppins', Arial, sans-serif",  // Updated to Poppins
      position: 'relative',
      boxSizing: 'border-box'
    }}>
      {/* Save Indicator */}
      {saveMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: saving ? '#FFC107' : '#4CAF50',
          color: saving ? 'black' : 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontWeight: 'bold'
        }}>
          {saveMessage}
        </div>
      )}

      {/* Header: PART F */}
      <div style={{
        color: '#FF8C42',
        fontSize: '28px',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '10px',
        letterSpacing: '2px'
      }}>
        PART F
      </div>

      {/* Title */}
      <div style={{
        color: '#2C5F7E',
        fontSize: '22px',
        fontWeight: 'bold',
        textAlign: 'left',
        marginBottom: '30px'
      }}>
        Number of Hours Spent by the Learner on the Following Activities:
      </div>

      {/* Section 1: Group Project Work */}
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: '2px solid #888',
        marginBottom: '5px'
      }}>
        <thead>
          <tr style={{ backgroundColor: '#F4CD8A' }}>
            <th style={{
              width: '60px',
              padding: '12px 10px',
              textAlign: 'left',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
              border: '1px solid #888'
            }}>S.No.</th>
            <th colSpan={2} style={{
              padding: '12px 10px',
              textAlign: 'left',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
              border: '1px solid #888'
            }}>
              1. Group Project Work
            </th>
            <th style={{
              width: '200px',
              padding: '12px 10px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
              border: '1px solid #888'
            }}>Number of Hours Spent</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ backgroundColor: '#FFFEF8' }}>
            <td style={{ padding: '10px', textAlign: 'center', fontSize: '14px', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', width: '40px', fontWeight: 'bold' }}>a)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>
              Research prompt/question/problem/challenge/planned final output
            </td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('group_project_work', 'research_prompt', formData.group_project_work.research_prompt)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFF9E6' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>b)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Guiding questions</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('group_project_work', 'guiding_questions', formData.group_project_work.guiding_questions)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFFEF8' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>c)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Stage 1 (Brainstorming and ideation)</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('group_project_work', 'stage_1_brainstorming', formData.group_project_work.stage_1_brainstorming)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFF9E6' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>d)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Stage 2 (Drafting, feedback, and revision)</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('group_project_work', 'stage_2_drafting', formData.group_project_work.stage_2_drafting)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFFEF8' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>e)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Stage 3 (Final submission)</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('group_project_work', 'stage_3_final_submission', formData.group_project_work.stage_3_final_submission)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#F4E4B8' }}>
            <td colSpan={3} style={{
              padding: '12px',
              textAlign: 'right',
              fontWeight: 'bold',
              fontSize: '16px',
              border: '1px solid #888'
            }}>
              Total Hours
            </td>
            <td style={{
              padding: '12px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '16px',
              border: '1px solid #888'
            }}>
              {calculateSectionTotal('group_project_work')}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Separator */}
      <div style={{
        height: '8px',
        backgroundColor: '#666',
        marginBottom: '5px'
      }} />

      {/* Section 2: Problem-Based Inquiry */}
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: '2px solid #888',
        marginBottom: '5px'
      }}>
        <thead>
          <tr style={{ backgroundColor: '#F4CD8A' }}>
            <th style={{
              width: '60px',
              padding: '12px 10px',
              textAlign: 'left',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
              border: '1px solid #888'
            }}>S.No.</th>
            <th colSpan={2} style={{
              padding: '12px 10px',
              textAlign: 'left',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
              border: '1px solid #888'
            }}>
              2. Problem-Based Inquiry (Individual Work)
            </th>
            <th style={{
              width: '200px',
              padding: '12px 10px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
              border: '1px solid #888'
            }}>Number of Hours Spent</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ backgroundColor: '#FFFEF8' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', width: '40px', fontWeight: 'bold' }}>a)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>
              Project prompt/question/problem/challenge/planned final output
            </td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('problem_based_inquiry', 'project_prompt', formData.problem_based_inquiry.project_prompt)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFF9E6' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>b)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Hypothesis</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('problem_based_inquiry', 'hypothesis', formData.problem_based_inquiry.hypothesis)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFFEF8' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>c)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Guiding questions</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('problem_based_inquiry', 'guiding_questions', formData.problem_based_inquiry.guiding_questions)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFF9E6' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>d)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Evidence collection to support/negate hypothesis</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('problem_based_inquiry', 'evidence_collection', formData.problem_based_inquiry.evidence_collection)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFFEF8' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>e)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Analysis and synthesis</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('problem_based_inquiry', 'analysis_synthesis', formData.problem_based_inquiry.analysis_synthesis)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFF9E6' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>f)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Discussions</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('problem_based_inquiry', 'discussions', formData.problem_based_inquiry.discussions)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFFEF8' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>g)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Conclusion</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('problem_based_inquiry', 'conclusion', formData.problem_based_inquiry.conclusion)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#F4E4B8' }}>
            <td colSpan={3} style={{
              padding: '12px',
              textAlign: 'right',
              fontWeight: 'bold',
              fontSize: '16px',
              border: '1px solid #888'
            }}>
              Total Hours
            </td>
            <td style={{
              padding: '12px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '16px',
              border: '1px solid #888'
            }}>
              {calculateSectionTotal('problem_based_inquiry')}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Separator */}
      <div style={{
        height: '8px',
        backgroundColor: '#666',
        marginBottom: '5px'
      }} />

      {/* Section 3: Classroom Interactions */}
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: '2px solid #888',
        marginBottom: '40px'
      }}>
        <thead>
          <tr style={{ backgroundColor: '#F4CD8A' }}>
            <th style={{
              width: '60px',
              padding: '12px 10px',
              textAlign: 'left',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
              border: '1px solid #888'
            }}>S.No.</th>
            <th colSpan={2} style={{
              padding: '12px 10px',
              textAlign: 'left',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
              border: '1px solid #888'
            }}>
              3. Classroom Interactions
            </th>
            <th style={{
              width: '200px',
              padding: '12px 10px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
              border: '1px solid #888'
            }}>Number of Hours Spent</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ backgroundColor: '#FFFEF8' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', width: '40px', fontWeight: 'bold' }}>a)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Classroom discussion</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('classroom_interactions', 'classroom_discussion', formData.classroom_interactions.classroom_discussion)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFF9E6' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>b)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Organised debate</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('classroom_interactions', 'organised_debate', formData.classroom_interactions.organised_debate)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFFEF8' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>c)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Simulation/roleplay</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('classroom_interactions', 'simulation_roleplay', formData.classroom_interactions.simulation_roleplay)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFF9E6' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>d)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Lab experiment</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('classroom_interactions', 'lab_experiment', formData.classroom_interactions.lab_experiment)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#FFFEF8' }}>
            <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #888' }}></td>
            <td style={{ padding: '10px 8px', fontSize: '14px', border: '1px solid #888', fontWeight: 'bold' }}>e)</td>
            <td style={{ padding: '10px 12px', fontSize: '14px', border: '1px solid #888' }}>Digital Learning</td>
            <td style={{ padding: '0', border: '1px solid #888' }}>
              {renderInput('classroom_interactions', 'digital_learning', formData.classroom_interactions.digital_learning)}
            </td>
          </tr>
          <tr style={{ backgroundColor: '#F4E4B8' }}>
            <td colSpan={3} style={{
              padding: '12px',
              textAlign: 'right',
              fontWeight: 'bold',
              fontSize: '16px',
              border: '1px solid #888'
            }}>
              Total Hours
            </td>
            <td style={{
              padding: '12px',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '16px',
              border: '1px solid #888'
            }}>
              {calculateSectionTotal('classroom_interactions')}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Page Number */}
      <div style={{
        textAlign: 'center',
        fontSize: '14px',
        color: '#666',
        fontWeight: 'bold',
        marginTop: '30px'
      }}>
        39
      </div>

    </div>
  );
};

export default PartFPage1;
